import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Order, OrderFeedback } from '../types/BookingTypes';
import { getOrderFeedback, submitOrderFeedback, getOrderById } from '../services/orderService';
import { createPaymentOrder, initializePayment } from '../services/paymentService';
import SuccessPopup from '../components/SuccessPopup';
import { API_BASE_URL } from '../constants';

interface TechnicianData {
  id: number;
  name: string;
  mobile: string;
  email: string;
  status: string;
  specialization: string;
  imagePath: string;
  experience: number;
  rating: number;
  totalOrdersCompleted: number;
  averageCompletionTime: number;
  currentWorkload: number;
  createdAt: string;
  updatedAt: string;
}

interface TechnicianResponse {
  entity: TechnicianData;
  isSuccess: boolean;
  errors: string | null;
  exception: string | null;
  message: string;
}

const OrderSummaryPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(location.state?.order || null);
  const [feedback, setFeedback] = useState<OrderFeedback | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [technician, setTechnician] = useState<TechnicianData | null>(null);
  const [isTechnicianLoading, setIsTechnicianLoading] = useState(false);
  const { orderId } = useParams<{ orderId: string }>();

  const fetchTechnicianDetails = async (technicianId: number) => {
    try {
      setIsTechnicianLoading(true);
      const response = await fetch(`${API_BASE_URL}/Technician/${technicianId}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch technician details');
      }

      const data: TechnicianResponse = await response.json();

      if (data.isSuccess && data.entity) {
        setTechnician(data.entity);
      } else {
        console.error('Failed to fetch technician details:', data.errors || data.message);
      }
    } catch (error) {
      console.error('Error fetching technician details:', error);
    } finally {
      setIsTechnicianLoading(false);
    }
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      // Get orderId from URL params
     

      // If no order and we have orderId, fetch the order
      if (!order && orderId) {
        try {
          setIsLoading(true);
          const fetchedOrder = await getOrderById(orderId);
          setOrder(fetchedOrder);
        } catch (error) {
          console.error('Error fetching order:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchOrderDetails();
  }, [order, orderId]);

  useEffect(() => {
    // Fetch technician details if order has technicianId > 0
    if (order && order.technicianId && order.technicianId > 0) {
      fetchTechnicianDetails(order.technicianId);
    }
  }, [order]);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (order) {
        try {
          const existingFeedback = await getOrderFeedback(order.id);
          if (existingFeedback) {
            setFeedback(existingFeedback);
            setRating(existingFeedback.rating);
            setComment(existingFeedback.comment);
            if (existingFeedback.photoUrl) {
              setPhotoPreview(`https://mintorepairs.com/uploads/assets/${existingFeedback.photoUrl}`);
            }
          }
        } catch (error) {
          console.error('Error fetching feedback:', error);
        }
      }
    };
    fetchFeedback();
  }, [order]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!order) return;

    try {
      setIsSubmitting(true);
      const newFeedback = await submitOrderFeedback(order.id, rating, comment, photo);
      setFeedback(newFeedback);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFullyPaid = (order?.orderStatus || '').toLowerCase() === 'payment received';

  const handleViewInvoice = () => {
    if (!order) return;
    window.open(`${API_BASE_URL}/Orders/${order.id}/Bill`, '_blank');
  };

  const handleOnlinePayment = async () => {
    if (!order) return;
    
    try {
      setIsProcessingPayment(true);
      
      // Initialize Cashfree payment
      await initializePayment({
        orderAmount: order.finalAmount,
        orderCurrency: 'INR',
        orderId: order.id.toString()
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minto-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-900">Order not found</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-minto-500 text-white rounded-lg hover:bg-minto-600"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00') {
      return '';
    }
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CREATED':
        return 'bg-blue-50 text-blue-700';
      case 'CONFIRMED':
        return 'bg-purple-50 text-purple-700';
      case 'IN_PROGRESS':
        return 'bg-yellow-50 text-yellow-700';
      case 'COMPLETED':
        return 'bg-green-50 text-green-700';
      case 'CANCELLED':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PAID':
        return 'text-green-600';
      case 'PENDING':
        return 'text-orange-600';
      case 'FAILED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Order Summary" />

      <div className="container mx-auto px-4 py-6 pb-24">
        {/* Order Status Card */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-3">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-1.5">
                <i className="fas fa-shopping-bag text-minto-500"></i>
                <h2 className="text-sm text-gray-600">Order #{order.orderNumber}</h2>
              </div>
              {formatDate(order.orderDate) && (
                <p className="text-xs text-gray-500 mt-0.5">{formatDate(order.orderDate)}</p>
              )}
            </div>
            <div className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(order.orderStatus)}`}>
              {order.orderStatus}
            </div>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
            <div>
              <div className="text-xs text-gray-500">Payment Status</div>
              <div className={`font-medium text-sm ${getPaymentStatusColor(order.paymentStatus)}`}>
                {order.paymentStatus}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Payment Method</div>
              <div className="font-medium text-sm text-gray-900">
                {order.paymentMethod.toUpperCase()}
              </div>
            </div>
          </div>
        </div>

        {/* Device Details Card */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="fas fa-mobile-alt text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">Device Details</h2>
          </div>
          <div className="space-y-2 p-2 bg-gray-50 rounded-lg text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Brand</span>
              <span className="font-medium text-gray-900">{order.makeName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Model</span>
              <span className="font-medium text-gray-900">{order.modelName}</span>
            </div>
            {order.deviceCondition && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Condition</span>
                <span className="font-medium text-gray-900">{order.deviceCondition}</span>
              </div>
            )}
          </div>
        </div>

        {/* Services Card */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="fas fa-tools text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">Services</h2>
            {order.orderItems && order.orderItems.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium text-white bg-minto-500 rounded-full">
                {order.orderItems.length}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {order.orderItems && order.orderItems.length > 0 ? (
              order.orderItems.map((item, index) => (
                <div key={item.id || index} 
                  className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-sm text-gray-900">{item.serviceName}</h3>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm text-minto-600">
                        ₹{Math.round((item.actualPrice || 0) + (item.serviceCost || 0) + ((item.serviceCost || 0) * 0.18))}
                      </div>
                      {item.warrantyPeriod && item.warrantyPeriod > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {item.warrantyPeriod} days warranty till {new Date(new Date(order.orderDate).getTime() + (item.warrantyPeriod * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1.5 rounded-lg">
                    <div className="font-medium mb-0.5">Warranty Coverage:</div>
                    <div className="text-blue-600 text-[11px]">{item.warrantyCoveredItems}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 p-2 bg-gray-50 rounded-lg text-sm">
                No services found
              </div>
            )}
          </div>
        </div>

        {/* Technician Details Card */}
        {Number(order.technicianId) > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-3">
            <div className="flex items-center gap-1.5 mb-3">
              <i className="fas fa-user-cog text-minto-500"></i>
              <h2 className="text-base font-semibold text-gray-900">Assigned Technician</h2>
            </div>
            {isTechnicianLoading ? (
              <div className="flex items-center justify-center p-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-minto-500"></div>
                <span className="ml-2 text-gray-500">Loading technician details...</span>
              </div>
            ) : technician ? (
              <div className="bg-gradient-to-r from-minto-50 to-blue-50 rounded-lg p-4 border border-minto-100">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <img
                      src={technician.imagePath}
                      alt="Technician"
                      className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-md"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://randomuser.me/api/portraits/men/32.jpg';
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <i className="fas fa-check text-white text-xs"></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-2">
                      <h3 className="font-semibold text-gray-900 text-base">{technician.name}</h3>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="fas fa-star text-yellow-400 text-sm"></i>
                        <span className="text-xs text-gray-600">
                          {technician.rating && technician.rating > 0 ? `${technician.rating}/5` : 'New'}
                        </span>
                        {(technician.experience && Number(technician.experience) > 0) ? (
                          <>
                            <span className="text-gray-400 text-xs">•</span>
                            <span className="text-xs text-gray-600">{technician.experience} years exp</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {(technician.experience && Number(technician.experience) > 0) ? (
                      <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <i className="fas fa-calendar-alt text-minto-500"></i>
                          <span>{technician.experience} years experience</span>
                        </div>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <i className="fas fa-mobile-alt text-minto-500"></i>
                      <span>{technician.mobile}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <a
                    href={`tel:${technician.mobile}`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-minto-500 text-white rounded-lg hover:bg-minto-600 transition-colors font-medium"
                  >
                    <i className="fas fa-phone"></i>
                    Call Technician
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 p-6 bg-gray-50 rounded-lg">
                <i className="fas fa-user-slash text-2xl text-gray-400 mb-2"></i>
                <div className="text-sm">Technician details not available</div>
              </div>
            )}
          </div>
        )}

        {/* Delivery Address Card */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="fas fa-map-marker-alt text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">Delivery Address</h2>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 leading-relaxed">{order.customerAddress}</p>
          </div>
        </div>

        {/* Bill Details Card */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="fas fa-receipt text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">Bill Details</h2>
          </div>
          <div className="space-y-2 p-2 bg-gray-50 rounded-lg text-sm">
            <div className="flex justify-between items-center text-gray-600">
              <span>Items Total (including service charges & taxes)</span>
              <span>₹{Math.round((order.totalItemPrice || 0) + (order.totalServiceCost || 0) + ((order.totalServiceCost || 0) * ((order.gstPercentage || 18) / 100)))}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <div className="flex items-center">
                <span>Doorstep Charges</span>
                <span className="ml-1 text-green-600 text-xs">(Waived)</span>
              </div>
              <span className="line-through text-gray-400">₹250.00</span>
            </div>
            {order.discountAmount > 0 && (
              <div className="flex justify-between items-center text-green-600">
                <div className="flex items-center">
                  <span>Online Payment Discount</span>
                  <span className="ml-1 text-green-600 text-xs">(₹50 off)</span>
                </div>
                <span className="font-medium">-₹{order.discountAmount}</span>
              </div>
            )}
            <div className="border-t border-gray-200 mt-2 pt-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">Total Amount</span>
                <span className="font-semibold text-lg text-minto-600">₹{Math.round(((order.totalItemPrice || 0) + (order.totalServiceCost || 0) + ((order.totalServiceCost || 0) * ((order.gstPercentage || 18) / 100))) - (order.discountAmount || 0))}</span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                You saved ₹{(250 + (order.discountAmount || 0)).toFixed(2)}
                {order.discountAmount > 0 ? ' (₹250 doorstep + ₹50 online payment)' : ' on doorstep charges'}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Details Card */}
        {order.orderPayments && order.orderPayments.length > 0 && (
          <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <i className="fas fa-credit-card text-minto-500"></i>
              <h2 className="text-base font-semibold text-gray-900">Payment Details</h2>
            </div>
            <div className="space-y-3">
              {order.orderPayments.map((payment, index) => (
                <div key={payment.id || index} className="p-2 bg-gray-50 rounded-lg text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-gray-500">Payment Method</div>
                      <div className="font-medium text-gray-900">{payment.paymentMethod}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Amount</div>
                      <div className="font-medium text-gray-900">₹{payment.paymentAmount.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Status</div>
                      <div className={`font-medium ${
                        payment.paymentStatus.toUpperCase() === 'SUCCESS' ? 'text-green-600' :
                        payment.paymentStatus.toUpperCase() === 'FAILED' ? 'text-red-600' :
                        'text-orange-600'
                      }`}>
                        {payment.paymentStatus}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Date</div>
                      <div className="text-gray-700">{formatDate(payment.createdAt)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support Card */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <i className="fas fa-headset text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">Need Help?</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <a
              href="tel:+918097537537"
              onClick={() => {
                // @ts-ignore
                window.gtag_report_conversion('tel:+918097537537');
                return false;
              }}
              className="flex flex-col items-center p-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-1">
                <i className="fas fa-phone text-blue-600"></i>
              </div>
              <h3 className="font-medium text-sm text-blue-900">Call Support</h3>
              <p className="text-xs text-blue-600">+91 9324 299 258</p>
            </a>
            <a
              href="https://wa.me/919324299258"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                // @ts-ignore
                window.gtag_report_conversion('https://wa.me/919324299258');
                return false;
              }}
              className="flex flex-col items-center p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-1">
                <i className="fab fa-whatsapp text-green-600"></i>
              </div>
              <h3 className="font-medium text-sm text-green-900">WhatsApp</h3>
              <p className="text-xs text-green-600">Chat with us</p>
            </a>
          </div>
        </div>

        {/* Feedback Section */}
        <div className="bg-white rounded-xl p-4 shadow-sm mt-6">
          <div className="flex items-center gap-1.5 mb-4">
            <i className="fas fa-star text-minto-500"></i>
            <h2 className="text-base font-semibold text-gray-900">
              {feedback ? 'Your Feedback' : 'Rate Your Experience'}
            </h2>
          </div>

          {feedback ? (
            <div className="space-y-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i
                    key={star}
                    className={`fas fa-star text-xl ${
                      star <= feedback.rating ? 'text-yellow-400' : 'text-gray-200'
                    }`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-line">{feedback.comment}</p>
              {photoPreview && (
                <div className="mt-4">
                  <img
                    src={photoPreview}
                    alt="Feedback"
                    className="w-full max-w-md rounded-lg shadow-sm"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500">
                Submitted on {new Date(feedback.createdAt).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <i
                      className={`fas fa-star text-2xl transition-colors ${
                        star <= rating ? 'text-yellow-400' : 'text-gray-200'
                      }`}
                    ></i>
                  </button>
                ))}
              </div>

              <div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us about your experience..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500 resize-none"
                  rows={4}
                ></textarea>
              </div>

              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full max-w-md rounded-lg shadow-sm"
                    />
                    <button
                      onClick={() => {
                        setPhoto(null);
                        setPhotoPreview('');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-red-600 transition-colors"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-gray-200 text-gray-500 hover:text-minto-600 hover:border-minto-500 transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-camera"></i>
                    Add Photo
                  </button>
                )}
              </div>

              <button
                onClick={handleSubmitFeedback}
                disabled={isSubmitting || !comment.trim()}
                className={`w-full px-4 py-3 rounded-lg font-medium text-white transition-colors ${
                  isSubmitting || !comment.trim()
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-minto-500 hover:bg-minto-600 active:bg-minto-700'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <i className="fas fa-spinner fa-spin"></i>
                    Submitting...
                  </div>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer with Payment Options */}
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.1)] border-t border-gray-200">
        <div className="container mx-auto px-4 py-3">
<div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm text-gray-600">Total Amount</span>
              <span className="text-lg font-semibold text-minto-600">₹{Math.round(((order.totalItemPrice || 0) + (order.totalServiceCost || 0) + ((order.totalServiceCost || 0) * ((order.gstPercentage || 18) / 100))) - (order.discountAmount || 0))}</span>
            </div>
            <div>
              {isFullyPaid ? (
                <button
                  onClick={handleViewInvoice}
                  className="px-8 py-3 rounded-xl font-medium text-base text-white transition-colors shadow-lg hover:shadow-xl bg-green-600 hover:bg-green-700 active:bg-green-800"
                >
                  <i className="fas fa-file-invoice mr-2"></i>
                  View Invoice
                </button>
              ) : (
                <button
                  onClick={handleOnlinePayment}
                  disabled={isProcessingPayment}
                  className={`px-10 py-3 rounded-xl font-medium text-base text-white transition-colors shadow-lg hover:shadow-xl ${
                    isProcessingPayment
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-minto-500 hover:bg-minto-600 active:bg-minto-700'
                  }`}
                >
                  {isProcessingPayment ? (
                    <div className="flex items-center gap-2">
                      <i className="fas fa-spinner fa-spin"></i>
                      Processing...
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-credit-card mr-2"></i>
                      Pay Now
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <SuccessPopup
          message="Thank you for your feedback!"
          onClose={() => setShowSuccess(false)}
        />
      )}

      {/* Payment Container */}
      <div id="payment-dropin" className="hidden">
        <div className="bg-white rounded-xl shadow-xl p-4 w-full max-w-lg mx-4">
          {/* Cashfree will inject the payment form here */}
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryPage; 